import { toInputDate } from "@/utils/dateUtils";
import type { ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Lock, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import CurrentBid from "./CurrentBid";
import {
  AuctionFinalizingIndicator,
  AuctionTimer,
  CompactAuctionTimer,
  isTimerZero,
  useAuctionTimer,
} from "./TimeRemaining";
import type { AuctionCalendarEvent } from "@/utils/calendarUtils";
import { AddToCalendarButton } from "./AddToCalendarButton";
import { ListingQuickActions } from "./ListingQuickActions";
import BiddingButtons from "./BiddingButtons";
import {
  ReservePriceStatus,
  getReservePriceReached,
} from "./ReservePriceStatus";
import { MyAuctionsStatsDialog } from "@/components/my-auctions/MyAuctionsStatsDialog";
import { useIsMobile, useIsTablet } from "@/hooks/use-mobile";
import { useLandscapeOrientation } from "@/hooks/useLandscapeOrientation";
import { useIsNativeTablet } from "@/hooks/useIsNativeTablet";
import { useTheme } from "@/context/ThemeContext";
import { useState } from "react";
import MobileBiddingBar from "./MobileBiddingBar";
import { DeliveryFeeZones, ListingStatus } from "@/types/listing";
import { ListingService } from "@/services/listing/listingService";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";

interface BiddingInfoProps {
  onPlaceBid: (bidAmount: number) => void;
  onBuyNow?: () => void;
  currentBid: number;
  nextMinBid: number;
  endTime: string;
  currentBuyNowPrice?: number;
  canBuyNow?: boolean;
  isBuyNowTriggered?: boolean;
  saleType?: "auction" | "direct";
  isOwnAuction?: boolean;
  isCompleted?: boolean;
  isPlacingBid?: boolean;
  status?: ListingStatus;
  isAdmin?: boolean;
  /** When true, show bidder names/emails in stats (Dashboard: detaljerad statistik). */
  canViewDetailedStats?: boolean;
  startingPrice?: number;
  currentPrice?: number;
  deliveryFee?: DeliveryFeeZones;
  // Simplified props instead of full listing object
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear?: number;
  regNumber?: string;
  listingTitle?: string;
  listingType: "auction" | "direct-purchase";
  auctionStartingPrice?: number;
  auctionCurrentBid?: number;
  directPurchasePrice?: number;
  vatCar: boolean;
  listingId?: string; // For fetching real statistics / calendar (cuid)
  listingSlug?: string | null; // Preferred public path segment for calendar links
  views?: number; // Real views data
  uniqueViews?: number; // Real unique views data
  myHighestBid?: number | null;
  isLeading?: boolean;
  requestedPrice?: number; // Seller's/customer's requested price (for stats dialog)
  reservationPrice?: number | null; // Auction reserve price
  reservedPrice?: number; // Price the listing was reserved for
  /** Placed under the bidding card (same scroll column on desktop / inside slide-up on mobile) */
  footerSlot?: ReactNode;
  /** Säljarens ort för upphämtning (API). */
  sellerPickupCity?: string | null;
  /** Admin stats: seller company or person name. */
  sellerName?: string | null;
  /** Admin stats: seller email. */
  sellerEmail?: string | null;
  /** Admin stats: optional subtitle under seller name. */
  sellerSubtitle?: string | null;
  guestLocked?: boolean;
  onGuestLoginRequired?: () => void;
  automaticBid?: {
    id: string;
    maxAmount: number;
    isActive: boolean;
  } | null;
  onSetAutoBid?: (maxAmount: number) => Promise<void> | void;
  onCancelAutoBid?: () => Promise<void> | void;
  isAutoBidLoading?: boolean;
  minimumBidIncrement?: number;
  /** Force the mobile bottom bidding bar (e.g. For You overlay sheet). */
  forceMobileBar?: boolean;
  /** Constrain the mobile bar to the parent panel (desktop side sheet). */
  containedMobileBar?: boolean;
  /** When true, buyers get a bid-history button (popover/sheet like fees/delivery). */
  showBidHistory?: boolean;
  /** Admin: toggle showBidHistory on the listing. */
  onToggleShowBidHistory?: () => void;
  isTogglingShowBidHistory?: boolean;
}

const BiddingInfo = ({
  onPlaceBid,
  onBuyNow,
  currentBid,
  nextMinBid,
  endTime,
  currentBuyNowPrice,
  canBuyNow,
  isBuyNowTriggered,
  saleType = "auction",
  isOwnAuction = false,
  isCompleted = false,
  isPlacingBid = false,
  status,
  isAdmin = false,
  canViewDetailedStats = false,
  startingPrice,
  currentPrice,
  deliveryFee,
  // Simplified props
  vehicleMake,
  vehicleModel,
  vehicleYear,
  regNumber,
  listingTitle,
  listingType,
  auctionStartingPrice,
  auctionCurrentBid,
  directPurchasePrice,
  vatCar,
  listingId,
  listingSlug,
  views,
  uniqueViews,
  myHighestBid,
  isLeading,
  requestedPrice,
  reservationPrice,
  reservedPrice,
  footerSlot,
  sellerPickupCity,
  sellerName,
  sellerEmail,
  sellerSubtitle,
  guestLocked = false,
  onGuestLoginRequired,
  automaticBid = null,
  onSetAutoBid,
  onCancelAutoBid,
  isAutoBidLoading = false,
  minimumBidIncrement = 500,
  forceMobileBar = false,
  containedMobileBar = false,
  showBidHistory = false,
  onToggleShowBidHistory,
  isTogglingShowBidHistory = false,
}: BiddingInfoProps) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isNativeTablet = useIsNativeTablet();
  const isLandscape = useLandscapeOrientation();
  const useMobileBidding =
    forceMobileBar || isMobile || isTablet || isNativeTablet;
  const hideReserveInCollapsed = isNativeTablet && isLandscape;
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { language } = useLanguage();
  const bi = carDetailsLabels(language).biddingInfo;
  const guestLabels = carDetailsLabels(language).guest;
  const bidding = carDetailsLabels(language).bidding;
  const guestDirectBuyClass =
    "bg-muted text-muted-foreground border border-border hover:bg-muted/90 cursor-pointer shadow-none";
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);

  const ownerOrAdminTitle = (
    <h3 className="text-lg font-semibold flex items-center gap-2">
      {isAdmin ? (
        <>
          <Shield className="h-5 w-5 text-sky-600 dark:text-sky-400 shrink-0" aria-hidden />
          <span>{bi.adminView}</span>
        </>
      ) : (
        bi.yourListing
      )}
    </h3>
  );
  const ownerOrAdminTitleMobile = (
    <h3 className="text-2xl md:text-lg font-semibold flex items-center gap-2">
      {isAdmin ? (
        <>
          <Shield className="h-6 w-6 md:h-5 md:w-5 text-sky-600 dark:text-sky-400 shrink-0" aria-hidden />
          <span>{bi.adminView}</span>
        </>
      ) : (
        bi.yourListing
      )}
    </h3>
  );

  const auctionTimeRemaining = useAuctionTimer(endTime);
  const isFinalizing =
    listingType === "auction" &&
    status === "active" &&
    Boolean(endTime) &&
    !!auctionTimeRemaining &&
    isTimerZero(auctionTimeRemaining);

  const calendarEvent: AuctionCalendarEvent | null =
    listingType === "auction" && !isCompleted && endTime
      ? {
          title:
            listingTitle ||
            `${vehicleMake} ${vehicleModel}${vehicleYear ? ` ${vehicleYear}` : ""}`,
          make: vehicleMake,
          model: vehicleModel,
          year: vehicleYear,
          regNumber,
          endTime,
          listingId,
          listingSlug,
          language,
        }
      : null;

  const renderAuctionTimer = (
    compact = false,
    timerClassName?: string,
  ) => {
    if (listingType !== "auction") return null;
    if (isFinalizing) {
      return (
        <AuctionFinalizingIndicator
          compact={compact || useMobileBidding}
          className={timerClassName}
        />
      );
    }
    return compact ? (
      <CompactAuctionTimer endTime={endTime} className={timerClassName} />
    ) : (
      <AuctionTimer endTime={endTime} className={timerClassName} />
    );
  };

  const renderDesktopCalendarFab = () =>
    !useMobileBidding && calendarEvent ? (
      <AddToCalendarButton event={calendarEvent} />
    ) : null;

  const renderDirectPurchaseBuyButton = () => {
    if (isOwnAuction || isAdmin || isCompleted) return null;
    if (!guestLocked && status === "reserved") {
      return (
        <Button
          className="w-full bg-gray-400 cursor-not-allowed text-gray-600"
          disabled
        >
          {bi.reserved}
        </Button>
      );
    }
    return (
      <Button
        type="button"
        className={`w-full gap-1.5 ${
          guestLocked
            ? guestDirectBuyClass
            : "bg-green-600 hover:bg-green-700 text-white"
        }`}
        onClick={() => {
          if (guestLocked) {
            onGuestLoginRequired?.();
            return;
          }
          onBuyNow?.();
        }}
      >
        {guestLocked && <Lock className="h-4 w-4" />}
        {guestLocked ? (guestLabels?.lockedBuyNow ?? bi.buyNow) : bi.buyNow}
      </Button>
    );
  };
  const [statsData, setStatsData] = useState<any>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  const reservationPriceValue =
    reservationPrice != null && reservationPrice > 0 ? reservationPrice : null;
  const reservationPriceFormatted =
    reservationPriceValue != null
      ? `${reservationPriceValue.toLocaleString("sv-SE")} :-`
      : undefined;
  const requestedPriceFormatted =
    requestedPrice != null
      ? `${requestedPrice.toLocaleString("sv-SE")} :-`
      : undefined;
  const hasCurrentBid = currentBid != null && currentBid > 0;
  const reservePriceReached = getReservePriceReached(
    reservationPriceValue,
    currentBid,
  );
  const reservePriceStatusElement =
    reservePriceReached == null ? null : (
      <ReservePriceStatus
        reached={reservePriceReached}
        label={
          reservePriceReached
            ? bidding.reservePriceReached
            : bidding.reservePriceNotReached
        }
        infoTitle={
          bidding.reservePriceInfoTitle ?? "Vad är reservationspris?"
        }
        infoDescription={
          bidding.reservePriceInfoDescription ??
          "Reservationspriset är det lägsta belopp säljaren är villig att acceptera."
        }
      />
    );
  const reservePriceStatusCollapsed =
    reservePriceReached == null ? null : (
      <ReservePriceStatus
        compact
        reached={reservePriceReached}
        label={
          reservePriceReached
            ? bidding.reservePriceShortReached
            : bidding.reservePriceShortNotReached
        }
        infoTitle={
          bidding.reservePriceInfoTitle ?? "Vad är reservationspris?"
        }
        infoDescription={
          bidding.reservePriceInfoDescription ??
          "Reservationspriset är det lägsta belopp säljaren är villig att acceptera."
        }
      />
    );

  const completedAuctionPrice =
    isBuyNowTriggered && currentBuyNowPrice != null
      ? currentBuyNowPrice
      : currentBid;
  const completedDisplayPrice =
    completedAuctionPrice || auctionStartingPrice || 0;

  // Admins without "detaljerad statistik" still open stats, but bidder IDs stay hidden (like a seller).
  const revealBidderIdentities = canViewDetailedStats === true;
  const canSeeFullListingStats = isAdmin || isOwnAuction;
  const showHiddenBiddingHint =
    listingType === "auction" &&
    !showBidHistory &&
    !canSeeFullListingStats &&
    !isOwnAuction &&
    !isCompleted &&
    status === "active";
  const hiddenBiddingHint = showHiddenBiddingHint ? (
    <p className="text-center text-muted-foreground/80 font-medium leading-tight text-[0.65rem] md:text-[0.6rem]">
      {bidding.hiddenBidding ?? "Dold budgivning"}
    </p>
  ) : null;

  const handleStatisticsClick = async () => {
    if (!canSeeFullListingStats) {
      return;
    }

    if (!listingId) {
      setStatsDialogOpen(true);
      return;
    }

    // Direct purchase: open stats without bid history (admin/seller only)
    if (listingType === "direct-purchase" || saleType === "direct") {
      setStatsData({
        totalViews: views || 0,
        uniqueViewers: uniqueViews || 0,
        totalBids: 0,
        uniqueBidders: 0,
        favoriteCount: undefined,
        currentPrice: `${(directPurchasePrice || 0).toLocaleString("sv-SE")} :-`,
        startingPrice: `${(startingPrice || directPurchasePrice || 0).toLocaleString("sv-SE")} :-`,
        requestedPrice: requestedPriceFormatted,
        reservationPrice: reservationPriceFormatted,
        endDate: endTime
          ? toInputDate(new Date(endTime))
          : bi.ongoing,
        bids: [],
      });
      setStatsDialogOpen(true);
      return;
    }

    setIsLoadingStats(true);

    try {
      // Fetch bids first, then open dialog – avoids showing empty bid history on first open
      const bidsResponse = await ListingService.getListingBids(listingId);
      const bids = bidsResponse.data || [];
      const favoriteCount = bidsResponse.favoriteCount;

      // Get highest bid amount
      const highestBidAmount =
        bids.length > 0 ? Math.max(...bids.map((b) => b.amount || 0)) : 0;

      // Map bids to format expected by dialog, sorted by amount descending
      const sortedBids = [...bids].sort((a, b) => b.amount - a.amount);
      const bidHistory = sortedBids.map((bid, index) => {
        const isHighest = index === 0 && bid.amount === highestBidAmount;
        let bidderName = "";

        if (revealBidderIdentities && bid.bidderName) {
          bidderName = bid.bidderName;
        } else {
          // Without detaljerad statistik: hidden like a ordinary customer/seller view
          bidderName = "•".repeat(8);
        }

        return {
          id: bid.id,
          bidder: bidderName,
          amount: `${bid.amount.toLocaleString("sv-SE")} :-`,
          timestamp: new Date(bid.createdAt).toLocaleString("sv-SE", {
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          }),
          isHighest,
          ...(revealBidderIdentities && bid.bidderEmail
            ? { email: bid.bidderEmail }
            : {}),
        };
      });

      // Calculate unique bidders
      const uniqueBidderIds = new Set(bids.map((b) => b.bidderId));
      const uniqueBidders = uniqueBidderIds.size;

      const currentPriceValue =
        listingType === "auction"
          ? currentBid || auctionStartingPrice || 0
          : directPurchasePrice || 0;
      const startingPriceValue =
        listingType === "auction"
          ? auctionStartingPrice || 0
          : directPurchasePrice || 0;

      setStatsData({
        totalViews: views || 0,
        uniqueViewers: uniqueViews || 0,
        totalBids: bids.length,
        uniqueBidders,
        ...(revealBidderIdentities && favoriteCount !== undefined
          ? { favoriteCount }
          : {}),
        currentPrice: `${currentPriceValue.toLocaleString("sv-SE")} :-`,
        startingPrice: `${startingPriceValue.toLocaleString("sv-SE")} :-`,
        requestedPrice: requestedPriceFormatted,
        reservationPrice: reservationPriceFormatted,
        endDate: endTime
          ? toInputDate(new Date(endTime))
          : isCompleted
            ? bi.completed
            : bi.ongoing,
        bids: bidHistory,
      });
      setStatsDialogOpen(true);
    } catch (error) {
      // Fallback stats – still open so user sees something
      setStatsData({
        totalViews: views || 0,
        uniqueViewers: uniqueViews || 0,
        totalBids: 0,
        uniqueBidders: 0,
        currentPrice: `${(listingType === "auction" ? currentBid || auctionStartingPrice || 0 : directPurchasePrice || 0).toLocaleString("sv-SE")} :-`,
        startingPrice: `${(listingType === "auction" ? auctionStartingPrice || 0 : directPurchasePrice || 0).toLocaleString("sv-SE")} :-`,
        requestedPrice: requestedPriceFormatted,
        reservationPrice: reservationPriceFormatted,
        endDate: endTime
          ? toInputDate(new Date(endTime))
          : bi.ongoing,
        bids: [],
      });
      setStatsDialogOpen(true);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const renderQuickActions = (price: number, withActions = false) => (
    <ListingQuickActions
      price={price}
      deliveryFee={deliveryFee}
      pickupDisplayText={sellerPickupCity}
      hideWhenOwn={isOwnAuction}
      calendarEvent={useMobileBidding ? calendarEvent : null}
      showStats={withActions && canSeeFullListingStats}
      onStatsClick={handleStatisticsClick}
      statsLoading={isLoadingStats}
      statsLabel={bi.statistics}
      showBidHistory={withActions && showBidHistory && !canSeeFullListingStats}
      listingId={listingId}
    />
  );

  const vehicleName = `${vehicleMake} ${vehicleModel}`;

  const composedFooter = footerSlot ? <>{footerSlot}</> : null;

  // INFO: Mobile / native view (always bottom sheet on Capacitor, including tablets)
  if (useMobileBidding) {
    if (saleType === "direct") {
      const hasPriceReduction =
        startingPrice && currentPrice && startingPrice > currentPrice;

      // Collapsed content: Status + Time + Price
      const displayPrice =
        hasPriceReduction && !isCompleted
          ? currentPrice
          : directPurchasePrice || currentPrice || 0;

      const collapsedContent = (
        <div className="flex flex-col items-center gap-2 w-full min-w-0">
          <div className="flex items-center justify-center gap-1 min-w-0 flex-wrap">
            <Badge
              className={
                isCompleted
                  ? "bg-red-500 text-white text-sm flex-shrink-0"
                  : status === "reserved"
                    ? "bg-orange-700 text-white text-sm flex-shrink-0"
                    : "bg-green-600 text-white text-sm flex-shrink-0"
              }
            >
              {isCompleted
                ? bi.completed
                : status === "reserved"
                  ? bi.reserved
                  : bi.available}
            </Badge>
            <span
              className="text-muted-foreground/60 font-light text-base shrink-0 px-3"
              aria-hidden
            >
              |
            </span>
            <div
              className={`text-xl font-bold whitespace-nowrap tabular-nums ${
                hasPriceReduction && !isCompleted
                  ? "text-red-600"
                  : "text-foreground"
              }`}
            >
              {new Intl.NumberFormat("sv-SE").format(displayPrice)} kr
            </div>
          </div>
        </div>
      );

      return (
        <>
          <MobileBiddingBar
            collapsedContent={collapsedContent}
            contained={containedMobileBar}
          >
            <>
              <BiddingInfoCard
                className="glass-card shadow-[0_0_15px_rgba(34,197,94,0.15)] rounded-t-xl mx-0"
              >
                <div className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    {isAdmin || isOwnAuction ? (
                      ownerOrAdminTitleMobile
                    ) : (
                      <h3 className="text-2xl md:text-lg font-semibold">
                        {bi.directPurchase}
                      </h3>
                    )}
                    <Badge
                      className={
                        isCompleted
                          ? "bg-red-500 text-white"
                          : status === "reserved"
                            ? "bg-orange-700 text-white"
                            : "bg-green-600 text-white"
                      }
                    >
                      {isCompleted
                        ? bi.completed
                        : status === "reserved"
                          ? bi.reserved
                          : bi.available}
                    </Badge>
                  </div>
                  <div className="w-full text-right space-y-1">
                    {hasPriceReduction && !isCompleted ? (
                      <>
                        <div className="text-lg md:text-sm text-gray-500 line-through">
                          {new Intl.NumberFormat("sv-SE").format(startingPrice)}{" "}
                          kr
                        </div>
                        <div className="text-2xl font-bold text-red-600">
                          {new Intl.NumberFormat("sv-SE").format(currentPrice)} kr
                        </div>
                        <div className="text-base md:text-xs text-green-600 font-medium">
                          {bi.reducedBy}{" "}
                          {new Intl.NumberFormat("sv-SE").format(
                            startingPrice - currentPrice,
                          )}{" "}
                          kr
                        </div>
                      </>
                    ) : (
                      <div className="text-2xl font-bold text-foreground">
                        {new Intl.NumberFormat("sv-SE").format(
                          directPurchasePrice || 0,
                        )}{" "}
                        kr
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                      {!guestLocked && (
                        <p className="text-sm md:text-xs text-primary/60 p-1 md:p-0.5 rounded-lg inline-block">
                          {vatCar ? bidding.vatCar : bidding.vmb}
                          {vatCar ? bidding.vatCarIncl : ""}
                        </p>
                      )}
                      <div className="flex items-center justify-end">
                        {renderQuickActions(directPurchasePrice || 0, true)}
                      </div>
                    </div>
                  </div>
                  {renderDirectPurchaseBuyButton()}
                </div>
              </BiddingInfoCard>
              {composedFooter ? <div className="px-3 pt-0.5 pb-2">{composedFooter}</div> : null}
            </>
          </MobileBiddingBar>
          {(isOwnAuction || isAdmin) && (
            <MyAuctionsStatsDialog
              open={statsDialogOpen}
              onOpenChange={setStatsDialogOpen}
              stats={
                statsData || {
                  totalViews: views || 0,
                  uniqueViewers: uniqueViews || 0,
                  totalBids: 0,
                  uniqueBidders: 0,
                  currentPrice: `${(directPurchasePrice || 0).toLocaleString("sv-SE")} :-`,
                  startingPrice: `${(startingPrice || directPurchasePrice || 0).toLocaleString("sv-SE")} :-`,
                  requestedPrice: requestedPriceFormatted,
                  reservationPrice: reservationPriceFormatted,
                  endDate: endTime
                    ? toInputDate(new Date(endTime))
                    : bi.ongoing,
                  bids: [],
                }
              }
              vehicleName={vehicleName}
              isLoading={isLoadingStats}
              isAdmin={revealBidderIdentities}
              hideBids
              sellerName={revealBidderIdentities ? sellerName || undefined : undefined}
              sellerEmail={revealBidderIdentities ? sellerEmail || undefined : undefined}
              sellerSubtitle={revealBidderIdentities ? sellerSubtitle || undefined : undefined}
            />
          )}
        </>
      );
    }

    if (isCompleted) {
      // Collapsed content for completed auction: status | price on line 1, badge on line 2
      const collapsedContent = (
        <div className="flex flex-col items-center gap-1 min-w-0 overflow-hidden">
          <div className="flex items-center justify-center gap-1 min-w-0">
            <Badge className="bg-red-500 text-white text-sm flex-shrink-0">
              {bi.completed}
            </Badge>
            <span className="text-muted-foreground/60 font-light text-xs shrink-0 px-3">
              |
            </span>
            <span className="text-xl font-bold text-green-600 truncate tabular-nums">
              {new Intl.NumberFormat("sv-SE").format(completedDisplayPrice)} kr
            </span>
          </div>
          {!hideReserveInCollapsed && reservePriceStatusCollapsed}
        </div>
      );

      return (
        <MobileBiddingBar
            collapsedContent={collapsedContent}
            contained={containedMobileBar}
          >
          <>
            <BiddingInfoCard
              className="glass-card ring-1 ring-red-500/50 shadow-[0_0_15px_rgba(239,68,68,0.15)] rounded-t-xl mx-0"
              calendarFab={renderDesktopCalendarFab()}
            >
              <div className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base sm:text-lg font-semibold">
                    {bi.auctionEnded}
                  </h3>
                  <Badge className="bg-red-500 text-white">
                    {bi.completed}
                  </Badge>
                </div>
                <CurrentBid
                  currentBid={completedDisplayPrice}
                  startingPrice={startingPrice}
                  taxType={vatCar ? "moms" : "exkl. moms"}
                  hideTaxType={guestLocked}
                  status={status}
                  reservedPrice={reservedPrice}
                  slotAfterPrice={
                    listingType === "auction"
                      ? renderQuickActions(completedDisplayPrice, true)
                      : undefined
                  }
                />
                {reservePriceStatusElement}
                {currentBid != undefined && currentBid != 0 && (
                  <div className="text-lg font-semibold text-green-600">
                    Högsta bud:{" "}
                    {new Intl.NumberFormat("sv-SE").format(currentBid)} kr
                  </div>
                )}
                {renderAuctionTimer()}
              </div>
            </BiddingInfoCard>
            {composedFooter ? <div className="px-3 pt-0.5 pb-2">{composedFooter}</div> : null}
          </>
        </MobileBiddingBar>
      );
    }

    if (isOwnAuction || isAdmin) {
      // Collapsed content for own auction: timer (grön) | price större i mobil
      const collapsedContent = (
        <div className="flex flex-col items-center gap-1 min-w-0 overflow-hidden">
          <div className="flex items-center justify-center gap-1 min-w-0">
            {listingType === "auction" && (
              <>
                <CompactAuctionTimer
                  endTime={endTime}
                  useGreen
                  className="text-lg md:text-xs flex-shrink-0"
                />
                <span className="text-muted-foreground/60 font-light text-base md:text-xs shrink-0 px-3">
                  |
                </span>
              </>
            )}
            <span className="text-xl md:text-lg font-bold text-foreground truncate tabular-nums">
              {new Intl.NumberFormat("sv-SE").format(
                currentBid || auctionStartingPrice,
              )}{" "}
              kr
            </span>
          </div>
          {!hideReserveInCollapsed && reservePriceStatusCollapsed}
        </div>
      );

      return (
        <>
          <MobileBiddingBar
            collapsedContent={collapsedContent}
            contained={containedMobileBar}
          >
            <>
              <BiddingInfoCard
                className="glass-card ring-1 ring-green-500/50 shadow-[0_0_15px_rgba(34,197,94,0.15)] rounded-t-xl mx-0"
                calendarFab={renderDesktopCalendarFab()}
                padPriceForCalendar={false}
              >
                <div className="p-4 sm:p-3 space-y-4 md:space-y-3">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      {ownerOrAdminTitleMobile}
                    </div>
                    <CurrentBid
                      currentBid={currentBid}
                      startingPrice={startingPrice}
                      taxType={vatCar ? "moms" : "exkl. moms"}
                  hideTaxType={guestLocked}
                      status={status}
                      reservedPrice={reservedPrice}
                      slotAfterPrice={
                        listingType === "auction"
                          ? renderQuickActions(
                              currentBid || auctionStartingPrice || 0,
                              true,
                            )
                          : undefined
                      }
                    />
                  </div>
                  {reservePriceStatusElement}
                  {renderAuctionTimer()}
                  <Progress
                    value={65}
                    className={`h-0.5 ${isDark ? "bg-black/20" : "bg-gray-100"}`}
                  />
                </div>
              </BiddingInfoCard>
              {composedFooter ? (
                <div className="px-3 pt-0.5 pb-2">{composedFooter}</div>
              ) : null}
            </>
          </MobileBiddingBar>
          <MyAuctionsStatsDialog
            open={statsDialogOpen}
            onOpenChange={setStatsDialogOpen}
            stats={
              statsData || {
                totalViews: views || 0,
                uniqueViewers: uniqueViews || 0,
                totalBids: 0,
                uniqueBidders: 0,
                currentPrice: `${(listingType === "auction" ? currentBid || auctionStartingPrice || 0 : directPurchasePrice || 0).toLocaleString("sv-SE")} :-`,
                startingPrice: `${(listingType === "auction" ? auctionStartingPrice || 0 : directPurchasePrice || 0).toLocaleString("sv-SE")} :-`,
                requestedPrice: requestedPriceFormatted,
                reservationPrice: reservationPriceFormatted,
                endDate: endTime
                  ? toInputDate(new Date(endTime))
                  : bi.ongoing,
                bids: [],
              }
            }
          vehicleName={vehicleName}
          isLoading={isLoadingStats}
          isAdmin={revealBidderIdentities}
          sellerName={revealBidderIdentities ? sellerName || undefined : undefined}
          sellerEmail={revealBidderIdentities ? sellerEmail || undefined : undefined}
          sellerSubtitle={revealBidderIdentities ? sellerSubtitle || undefined : undefined}
        />
      </>
    );
  }

  // Collapsed content for regular auction: timer (grön) | price större i mobil
  const collapsedContent = (
      <div className="flex flex-col items-center gap-1 min-w-0 overflow-hidden">
        <div className="flex items-center justify-center gap-1 min-w-0">
          {listingType === "auction" && (
            <>
              <CompactAuctionTimer
                endTime={endTime}
                useGreen
                className="text-lg md:text-xs flex-shrink-0"
              />
              <span className="text-muted-foreground/60 font-light text-base md:text-xs shrink-0 px-3">
                |
              </span>
            </>
          )}
          <span className="text-xl md:text-lg font-bold text-foreground truncate tabular-nums">
            {new Intl.NumberFormat("sv-SE").format(
              currentBid || auctionStartingPrice,
            )}{" "}
            kr
          </span>
        </div>
        {!hideReserveInCollapsed && reservePriceStatusCollapsed}
      </div>
    );

    // Determine card styling based on bidding status
    const getCardClassName = () => {
      const baseClasses =
        "glass-card ring-1 ring-accent/50 shadow-[0_0_15px_rgba(212,175,55,0.15)] rounded-t-xl mx-0";
      if (
        listingType === "auction" &&
        myHighestBid != null &&
        myHighestBid > 0
      ) {
        if (isLeading) {
          return `${baseClasses} !bg-green-500/10 border-green-500/30`;
        } else {
          return `${baseClasses} !bg-orange-500/10 border-orange-500/30`;
        }
      }
      return baseClasses;
    };

    return (
      <MobileBiddingBar
            collapsedContent={collapsedContent}
            contained={containedMobileBar}
          >
        <>
          <BiddingInfoCard
            className={getCardClassName()}
            calendarFab={renderDesktopCalendarFab()}
            padPriceForCalendar={
              !(
                listingType === "auction" &&
                myHighestBid != null &&
                myHighestBid > 0
              )
            }
          >
            <div className="p-4 sm:p-3 space-y-4 md:space-y-3">
              <div className="space-y-1">
                {listingType === "auction" &&
                  myHighestBid != null &&
                  myHighestBid > 0 && (
                    <Badge
                      className={
                        isLeading
                          ? "bg-green-600 text-white"
                          : "bg-orange-700 text-white"
                      }
                    >
                      {isLeading ? bi.youLead : bi.outbid}
                    </Badge>
                  )}
                <CurrentBid
                  currentBid={currentBid}
                  startingPrice={startingPrice}
                  taxType={vatCar ? "moms" : "exkl. moms"}
                  hideTaxType={guestLocked}
                  status={status}
                  reservedPrice={reservedPrice}
                  myHighestBid={myHighestBid}
                  isLeading={isLeading}
                  slotAfterPrice={
                    listingType === "auction"
                      ? renderQuickActions(
                          currentBid || auctionStartingPrice || 0,
                          true,
                        )
                      : undefined
                  }
                />
              </div>
              {reservePriceStatusElement}
              {renderAuctionTimer()}
              <Progress
                value={65}
                className={`h-0.5 ${isDark ? "bg-black/20" : "bg-gray-100"}`}
              />
              <BiddingButtons
                onPlaceBid={onPlaceBid}
                onBuyNow={onBuyNow}
                onSetAutoBid={onSetAutoBid}
                onCancelAutoBid={onCancelAutoBid}
                automaticBid={automaticBid}
                isAutoBidLoading={isAutoBidLoading}
                minimumBidIncrement={minimumBidIncrement}
                nextMinBid={nextMinBid}
                isPlacingBid={isPlacingBid}
                currentBuyNowPrice={currentBuyNowPrice}
                canBuyNow={canBuyNow}
                currentBid={currentBid}
                isOwnAuction={isOwnAuction}
                status={status}
                guestLocked={guestLocked}
                onGuestLoginRequired={onGuestLoginRequired}
                isFinalizing={isFinalizing}
              />
              {hiddenBiddingHint}
            </div>
          </BiddingInfoCard>
          {composedFooter ? <div className="px-3 pt-0.5 pb-2">{composedFooter}</div> : null}
        </>
      </MobileBiddingBar>
    );
  }

  // INFO: Desktop view
  if (saleType === "direct") {
    const hasPriceReduction =
      startingPrice && currentPrice && startingPrice > currentPrice;
    return (
      <>
        <BiddingInfoCard
          className="glass-card animate-fade-in ring-1 ring-green-500/50 shadow-[0_0_15px_rgba(34,197,94,0.15)]"
        >
          <div className="p-2 md:p-4 space-y-3 md:space-y-4">
            <div className="flex items-center justify-between">
              {isAdmin || isOwnAuction ? (
                ownerOrAdminTitle
              ) : (
                <h3 className="text-lg font-semibold">{bi.directPurchase}</h3>
              )}
              <Badge
                className={
                  isCompleted
                    ? "bg-red-500 text-white"
                    : status === "reserved"
                      ? "bg-orange-700 text-white"
                      : "bg-green-600 text-white"
                }
              >
                {isCompleted
                  ? bi.completed
                  : status === "reserved"
                    ? bi.reserved
                    : bi.available}
              </Badge>
            </div>
            <div className="flex flex-col items-end gap-3 w-full">
              {/* Price (right) */}
              <div className="w-full text-right space-y-1">
                {hasPriceReduction && !isCompleted ? (
                  <>
                    <div className="text-sm text-gray-500 line-through">
                      {new Intl.NumberFormat("sv-SE").format(startingPrice)} kr
                    </div>
                    <div className="text-2xl font-bold text-red-600">
                      {new Intl.NumberFormat("sv-SE").format(currentPrice)} kr
                    </div>
                    <div className="text-xs text-green-600 font-medium">
                      {bi.reducedBy}{" "}
                      {new Intl.NumberFormat("sv-SE").format(
                        startingPrice - currentPrice,
                      )}{" "}
                      kr
                    </div>
                  </>
                ) : (
                  !isCompleted && (
                    <div className="text-2xl font-bold text-foreground">
                      {new Intl.NumberFormat("sv-SE").format(
                        directPurchasePrice || 0,
                      )}{" "}
                      kr
                    </div>
                  )
                )}
                <div className="mt-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                  {!guestLocked && (
                    <p className="text-sm md:text-xs text-primary/60 p-1 md:p-0.5 rounded-lg inline-block">
                      {vatCar ? bidding.vatCar : bidding.vmb}
                      {vatCar ? bidding.vatCarIncl : ""}
                    </p>
                  )}
                  {renderQuickActions(
                    directPurchasePrice || currentPrice || 0,
                    true,
                  )}
                </div>
              </div>

              {renderDirectPurchaseBuyButton()}
            </div>
          </div>
        </BiddingInfoCard>
        {composedFooter ? <div className="mt-1">{composedFooter}</div> : null}
        {(isOwnAuction || isAdmin) && (
          <MyAuctionsStatsDialog
            open={statsDialogOpen}
            onOpenChange={setStatsDialogOpen}
            stats={
              statsData || {
                totalViews: views || 0,
                uniqueViewers: uniqueViews || 0,
                totalBids: 0,
                uniqueBidders: 0,
                currentPrice: `${(directPurchasePrice || 0).toLocaleString("sv-SE")} :-`,
                startingPrice: `${(startingPrice || directPurchasePrice || 0).toLocaleString("sv-SE")} :-`,
                requestedPrice: requestedPriceFormatted,
                reservationPrice: reservationPriceFormatted,
                endDate: endTime
                  ? toInputDate(new Date(endTime))
                  : bi.ongoing,
                bids: [],
              }
            }
            vehicleName={vehicleName}
            isLoading={isLoadingStats}
            isAdmin={revealBidderIdentities}
            hideBids
            sellerName={revealBidderIdentities ? sellerName || undefined : undefined}
            sellerEmail={revealBidderIdentities ? sellerEmail || undefined : undefined}
            sellerSubtitle={revealBidderIdentities ? sellerSubtitle || undefined : undefined}
          />
        )}
      </>
    );
  }

  if (isCompleted) {
    return (
      <>
        <BiddingInfoCard
          className="glass-card animate-fade-in ring-1 ring-red-500/50 shadow-[0_0_15px_rgba(239,68,68,0.15)]"
          calendarFab={renderDesktopCalendarFab()}
        >
          <div className="p-2 md:p-4 space-y-3 md:space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{bi.auctionEnded}</h3>
              <Badge className="bg-red-500 text-white">{bi.completed}</Badge>
            </div>
            <CurrentBid
              currentBid={completedDisplayPrice}
              startingPrice={startingPrice}
              taxType={vatCar ? "moms" : "exkl. moms"}
              hideTaxType={guestLocked}
              status={status}
              reservedPrice={reservedPrice}
              slotAfterPrice={
                listingType === "auction"
                  ? renderQuickActions(completedDisplayPrice, true)
                  : undefined
              }
            />
            {reservePriceStatusElement}
            {currentBid != undefined && currentBid != 0 && (
              <div className="text-lg font-semibold text-green-600">
                {bi.highestBid}{" "}
                {new Intl.NumberFormat("sv-SE").format(currentBid)} kr
              </div>
            )}
            {renderAuctionTimer()}
          </div>
        </BiddingInfoCard>
        {composedFooter ? <div className="mt-1">{composedFooter}</div> : null}
      </>
    );
  }

  if (isOwnAuction || isAdmin) {
    return (
      <>
        <BiddingInfoCard
          className="glass-card animate-fade-in ring-1 ring-green-500/50 shadow-[0_0_15px_rgba(34,197,94,0.15)]"
          calendarFab={renderDesktopCalendarFab()}
          padPriceForCalendar={false}
        >
          <div className="p-2 md:px-4 md:py-2 space-y-3 md:space-y-3.5">
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                {ownerOrAdminTitle}
              </div>
              <CurrentBid
                currentBid={currentBid}
                startingPrice={startingPrice}
                taxType={vatCar ? "moms" : "exkl. moms"}
                hideTaxType={guestLocked}
                status={status}
                reservedPrice={reservedPrice}
                slotAfterPrice={
                  listingType === "auction"
                    ? renderQuickActions(
                        currentBid || auctionStartingPrice || 0,
                        true,
                      )
                    : undefined
                }
              />
            </div>
            {reservePriceStatusElement}
            {renderAuctionTimer()}
            <Progress
              value={65}
              className={`h-0.5 ${isDark ? "bg-black/20" : "bg-gray-100"}`}
            />
          </div>
        </BiddingInfoCard>
        {composedFooter ? <div className="mt-1">{composedFooter}</div> : null}
        <MyAuctionsStatsDialog
          open={statsDialogOpen}
          onOpenChange={setStatsDialogOpen}
          stats={
            statsData || {
              totalViews: views || 0,
              uniqueViewers: uniqueViews || 0,
              totalBids: 0,
              uniqueBidders: 0,
              currentPrice: `${(listingType === "auction" ? currentBid || auctionStartingPrice || 0 : directPurchasePrice || 0).toLocaleString("sv-SE")} :-`,
              startingPrice: `${(listingType === "auction" ? auctionStartingPrice || 0 : directPurchasePrice || 0).toLocaleString("sv-SE")} :-`,
              requestedPrice: requestedPriceFormatted,
              reservationPrice: reservationPriceFormatted,
              endDate: endTime
                ? toInputDate(new Date(endTime))
                : bi.ongoing,
              bids: [],
            }
          }
          vehicleName={vehicleName}
          isLoading={isLoadingStats}
          isAdmin={revealBidderIdentities}
          sellerName={revealBidderIdentities ? sellerName || undefined : undefined}
          sellerEmail={revealBidderIdentities ? sellerEmail || undefined : undefined}
          sellerSubtitle={revealBidderIdentities ? sellerSubtitle || undefined : undefined}
        />
      </>
    );
  }

  // Determine card styling based on bidding status
  const getCardClassName = () => {
    const baseClasses =
      "glass-card animate-fade-in ring-1 ring-accent/50 shadow-[0_0_15px_rgba(212,175,55,0.15)]";
    if (listingType === "auction" && myHighestBid != null && myHighestBid > 0) {
      if (isLeading) {
        return `${baseClasses} !bg-green-500/10 border-green-500/30`;
      } else {
        return `${baseClasses} !bg-orange-500/10 border-orange-500/30`;
      }
    }
    return baseClasses;
  };

  return (
    <>
      <BiddingInfoCard
        className={getCardClassName()}
        calendarFab={renderDesktopCalendarFab()}
        padPriceForCalendar={
          !(
            listingType === "auction" &&
            myHighestBid != null &&
            myHighestBid > 0
          )
        }
      >
        <div className="p-2 md:p-4 space-y-3 md:space-y-4">
          <div className="space-y-1">
            {listingType === "auction" &&
              myHighestBid != null &&
              myHighestBid > 0 && (
                <Badge
                  className={
                    isLeading
                      ? "bg-green-600 text-white"
                      : "bg-orange-700 text-white"
                  }
                >
                  {isLeading ? bi.youLead : bi.outbid}
                </Badge>
              )}
            <CurrentBid
              currentBid={currentBid}
              startingPrice={startingPrice}
              taxType={vatCar ? "moms" : "exkl. moms"}
              hideTaxType={guestLocked}
              status={status}
              reservedPrice={reservedPrice}
              myHighestBid={myHighestBid}
              isLeading={isLeading}
              slotAfterPrice={
                listingType === "auction"
                  ? renderQuickActions(
                      currentBid || auctionStartingPrice || 0,
                      true,
                    )
                  : undefined
              }
            />
          </div>
          {reservePriceStatusElement}
          {renderAuctionTimer()}
          <Progress
            value={65}
            className={`h-0.5 ${isDark ? "bg-black/20" : "bg-gray-100"}`}
          />
          <BiddingButtons
            onPlaceBid={onPlaceBid}
            onBuyNow={onBuyNow}
            onSetAutoBid={onSetAutoBid}
            onCancelAutoBid={onCancelAutoBid}
            automaticBid={automaticBid}
            isAutoBidLoading={isAutoBidLoading}
            minimumBidIncrement={minimumBidIncrement}
            nextMinBid={nextMinBid}
            isPlacingBid={isPlacingBid}
            currentBuyNowPrice={currentBuyNowPrice}
            canBuyNow={canBuyNow}
            currentBid={currentBid}
            isOwnAuction={isOwnAuction}
            status={status}
            guestLocked={guestLocked}
            onGuestLoginRequired={onGuestLoginRequired}
            isFinalizing={isFinalizing}
          />
          {hiddenBiddingHint}
        </div>
      </BiddingInfoCard>
      {composedFooter ? <div className="mt-1">{composedFooter}</div> : null}
    </>
  );
};

function BiddingInfoCard({
  className,
  children,
  calendarFab,
  padPriceForCalendar = true,
}: {
  className?: string;
  children: ReactNode;
  calendarFab?: ReactNode;
  /** Extra top padding on the price row so the calendar FAB does not cover it. Skip when a status header already sits above. */
  padPriceForCalendar?: boolean;
}) {
  return (
    <div className="relative">
      <Card
        className={cn(
          "relative overflow-visible",
          calendarFab &&
            padPriceForCalendar &&
            "md:[&_.bidding-current-price-row]:pt-3",
          className,
        )}
      >
        {calendarFab ? (
          <div className="absolute -top-5 right-3 z-20 hidden md:block">
            {calendarFab}
          </div>
        ) : null}
        {children}
      </Card>
    </div>
  );
}

export default BiddingInfo;
